import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";

import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { Skeleton } from "../../../../components/ui/skeleton";
import { Alert, AlertDescription } from "../../../../components/ui/alert";

// ── Constants (mirrors LeadCommsSection) ──────────────────────────────────────

const WHATSAPP_STATUS_LABELS = {
  accepted: "Accepted",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
};

const PAYLOAD_HIDDEN_KEYS = new Set([
  "lead_id",
  "lead_purchase_id",
  "seller_profile_id",
]);

const PAYLOAD_LABELS = {
  buyer_name: "Buyer Name",
  category_noun: "Category",
  category_plural: "Category (Plural)",
  seller_name: "Seller Name",
  seller_business_name: "Business Name",
  seller_email: "Seller Email",
  seller_phone: "Seller Phone",
  template_variant: "Template Variant",
  extra_link_1: "Portfolio / Custom Link",
  extra_link_2: "Social Link",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatTs = (ts) => (ts ? format(new Date(ts), "dd MMM yyyy, HH:mm") : null);
const formatTsShort = (ts) => (ts ? format(new Date(ts), "HH:mm") : null);
const formatDateKey = (ts) => (ts ? format(new Date(ts), "dd MMM yyyy") : "Unknown date");

// ── Delivery status pill ──────────────────────────────────────────────────────

const DeliveryPill = ({ detail }) => {
  if (!detail?.whatsapp_status) return null;
  const s = detail.whatsapp_status.toLowerCase();
  const label = WHATSAPP_STATUS_LABELS[s] ?? detail.whatsapp_status;

  if (s === "read") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-growth-green">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "delivered") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
        <XCircle className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{label}</span>;
};

// ── Outbound message payload panel ────────────────────────────────────────────

const MessagePayload = ({ payload }) => {
  if (!payload) return null;

  const displayEntries = Object.entries(payload).filter(
    ([key, value]) =>
      !PAYLOAD_HIDDEN_KEYS.has(key) && value !== null && value !== undefined && value !== ""
  );

  if (displayEntries.length === 0) return null;

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
      <p className="mb-1.5 font-semibold text-muted-foreground uppercase tracking-wide text-[10px]">
        Message Details
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {displayEntries.map(([key, value]) => (
          <React.Fragment key={key}>
            <dt className="text-muted-foreground whitespace-nowrap">
              {PAYLOAD_LABELS[key] ?? key}
            </dt>
            <dd className="font-medium text-foreground break-all">{String(value)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

// ── Outbound bubble (right-aligned) ───────────────────────────────────────────

const OutboundBubble = ({ message }) => {
  const detail = message.whatsapp_detail;

  const timeline = detail
    ? [
        detail.sent_at && { label: "Sent", ts: detail.sent_at },
        detail.delivered_at && { label: "Delivered", ts: detail.delivered_at },
        detail.read_at && { label: "Read", ts: detail.read_at },
        detail.failed_at && { label: "Failed", ts: detail.failed_at, error: true },
      ].filter(Boolean)
    : [];

  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] space-y-1">
        <div className="rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-primary-foreground shadow-sm">
          {message.sent_to && (
            <p className="text-xs font-semibold opacity-80">{message.sent_to}</p>
          )}
          {message.template_id && (
            <p className="mt-0.5 font-mono text-xs opacity-70">{message.template_id}</p>
          )}
          {!message.template_id && !message.sent_to && (
            <p className="text-xs italic opacity-70">Outbound message</p>
          )}
        </div>

        {/* Delivery status + timestamp */}
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 pr-1 text-[10px] text-muted-foreground">
          <DeliveryPill detail={detail} />
          {timeline.map(({ label, ts, error }) => (
            <span
              key={label}
              className={`flex items-center gap-0.5 ${error ? "text-destructive" : ""}`}
            >
              {error ? <XCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
              {label}: {formatTsShort(ts)}
            </span>
          ))}
          {timeline.length === 0 && (
            <span className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" />
              {formatTs(message.timestamp)}
            </span>
          )}
        </div>

        {detail?.failed_at && detail?.error_details && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-[10px] text-destructive">
            {detail.error_code && (
              <span className="font-mono mr-1">[{detail.error_code}]</span>
            )}
            {detail.error_details}
          </div>
        )}

        <MessagePayload payload={message.payload} />
      </div>
    </div>
  );
};

// ── Inbound bubble (left-aligned) ─────────────────────────────────────────────

const InboundBubble = ({ message }) => (
  <div className="flex justify-start">
    <div className="max-w-[80%] space-y-1">
      <div className="rounded-2xl rounded-tl-sm border border-border bg-accent/50 px-4 py-2.5 shadow-sm">
        <p className="text-xs font-semibold text-foreground">
          {message.sender_profile_name || message.sender_phone || "Buyer"}
        </p>
        {message.content ? (
          <p className="mt-0.5 text-sm text-foreground whitespace-pre-wrap break-words">
            {message.content}
          </p>
        ) : (
          <p className="mt-0.5 text-sm italic text-muted-foreground">
            [{message.message_type || "message"}]
          </p>
        )}
      </div>
      <p className="pl-1 text-[10px] text-muted-foreground">{formatTs(message.timestamp)}</p>
    </div>
  </div>
);

// ── Date separator ────────────────────────────────────────────────────────────

const DateSeparator = ({ label }) => (
  <div className="my-2 flex items-center justify-center">
    <span className="rounded-full bg-muted px-3 py-0.5 text-[11px] font-medium text-muted-foreground shadow-sm">
      {label}
    </span>
  </div>
);

// ── Group messages by calendar date (preserves chronological order) ──────────

const groupByDate = (messages) => {
  const groups = [];
  let currentKey = null;
  let currentBucket = null;

  messages.forEach((message) => {
    const key = formatDateKey(message.timestamp);
    if (key !== currentKey) {
      currentKey = key;
      currentBucket = { date: key, items: [] };
      groups.push(currentBucket);
    }
    currentBucket.items.push(message);
  });

  return groups;
};

// ── Loading skeleton ──────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="flex justify-center">
      <Skeleton className="h-5 w-28 rounded-full" />
    </div>
    {[1, 2, 3].map((i) => (
      <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
        <Skeleton className="h-14 w-56 rounded-2xl" />
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

/**
 * A reusable dialog that shows a buyer's ENTIRE WhatsApp conversation grouped by date.
 *
 * @param {Function} fetchConversation - async () => { buyer_id, phone, messages: [...] }
 * @param {string}   title             - Buyer name shown in the dialog title
 * @param {string}   [buttonLabel]     - Trigger button label (default "View all conversations")
 * @param {string}   [buttonSize]      - shadcn Button size (default "sm")
 * @param {string}   [buttonVariant]   - shadcn Button variant (default "outline")
 */
const ConversationDialog = ({
  fetchConversation,
  title,
  buttonLabel = "View all conversations",
  buttonSize = "sm",
  buttonVariant = "outline",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || hasFetched || typeof fetchConversation !== "function") return;

    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchConversation();
        if (!cancelled) setConversation(data);
      } catch (err) {
        console.error("ConversationDialog: failed to fetch conversation", err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasFetched(true);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, hasFetched, fetchConversation]);

  const messages = conversation?.messages ?? [];
  const groups = groupByDate(messages);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button type="button" size={buttonSize} variant={buttonVariant}>
          <MessageSquare className="w-4 h-4 mr-1" />
          {buttonLabel}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Full Conversation
            {title && <span className="text-muted-foreground font-normal">— {title}</span>}
            {hasFetched && !error && (
              <Badge variant="outline" className="ml-1 text-xs font-normal">
                {messages.length} {messages.length === 1 ? "message" : "messages"}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Complete WhatsApp history (sent &amp; received), including messages not tied to a
            specific lead.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[65vh] overflow-y-auto space-y-3 pr-1">
          {isLoading && <LoadingSkeleton />}

          {!isLoading && error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error.response?.data?.detail ||
                  error.message ||
                  "Failed to load conversation."}
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !error && hasFetched && messages.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No conversations recorded for this buyer.
            </p>
          )}

          {!isLoading &&
            !error &&
            groups.map((group) => (
              <div key={group.date} className="space-y-3">
                <DateSeparator label={group.date} />
                {group.items.map((message, idx) =>
                  message.direction === "inbound" ? (
                    <InboundBubble key={`${group.date}-${idx}`} message={message} />
                  ) : (
                    <OutboundBubble key={`${group.date}-${idx}`} message={message} />
                  )
                )}
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConversationDialog;
